import React, { useMemo } from 'react'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { TableColumn, TableSquare, Text, InfoIcon, TextVariant, ContentCard } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useGetCurrentCycleNodesDataQuery } from 'state/services/indexNode/indexNode'
import { useLifetimeTokens } from 'state/global/hooks'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { CurrentCycleDataResponse, getNodeNameByType, LicenseStatus } from '@mprolab/sdk'
import { useTranslation } from 'react-i18next'
import { CheckCircle } from 'react-feather'

import { MediaQuery } from '@mprolab/uikit/src/theme/base'
import CycleProgressBar from './CycleProgressBar'

const StyledHeaderTitle = styled(Text).attrs(() => ({
  variant: TextVariant.BODY_SMALL
}))`
  color: ${({ theme }) => theme.colors.white3};
  display: flex;
  align-items: center;
  padding-right: 8px;
  gap: 8px;
`

const CurrentCycleData: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const isTablet = useThemeMediaQuery('tablet')
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]
  const { data, isLoading: loading } = useGetCurrentCycleNodesDataQuery(
    { account, accessToken },
    { skip: !account || !accessToken, refetchOnMountOrArgChange: true }
  )

  const columns = useMemo(
    (): TableColumn[] => [
      {
        key: 'node',
        name: t('common.node'),
        gridColumns: 8,
        customComponent(node: CurrentCycleDataResponse) {
          return (
            <Text variant={TextVariant.BODY_SMALL} linesToDisplay={1} isTrimmed>
              {node.license?.name || node.license.licenseId}
            </Text>
          )
        }
      },
      {
        key: 'type',
        name: t('licenses.type'),
        gridColumns: 2,
        customComponent(node: CurrentCycleDataResponse) {
          return (
            <Text variant={TextVariant.BODY_SMALL} linesToDisplay={1} isTrimmed>
              {getNodeNameByType(node.license.type)}
            </Text>
          )
        }
      },
      {
        key: 'activeTime',
        name: (
          <StyledHeaderTitle>
            {t('rewards.activeTime')}
            <InfoIcon
              iconProps={{ size: 18 }}
              tooltipProps={{
                title: t('rewards.activeTimeInfo'),
                placement: 'top'
              }}
            />
          </StyledHeaderTitle>
        ),
        gridColumns: 2,
        customComponent(node: CurrentCycleDataResponse) {
          switch (true) {
            case node.license.status !== LicenseStatus.TERMINATED:
              return <CheckCircle size={16} color={colors.green} />
            default:
              return (
                <Text variant={TextVariant.BODY_SMALL} color={colors.white5}>
                  -
                </Text>
              )
          }
        }
      }
    ],
    [data]
  )

  return (
    <ContentCard fullWidth height="100%" flexDirection="column" p={isTablet ? '24px' : '24px 32px'}>
      <TableSquare
        tableTitle={t('rewards.currentCycleData')}
        plain
        fullViewMediaquery={MediaQuery.tablet}
        fullViewCustomWidth={450}
        // Five rows, then scroll. A holder with fifteen licences had a list
        // taller than the panel beside it, pushing the whole page down.
        // Rows are ~37px here; 272px fitted seven of them, which was still
        // too tall next to the distribution panel.
        maxBodyHeight="190px"
        {...{ columns, data, loading }}
      />
      <CycleProgressBar />
    </ContentCard>
  )
}

export default CurrentCycleData
